<template>
  <b-modal
    id="pay-invoice-process"
    ref="pay-invoice-process"
    size="sm"
    body-class="p-2"
    static
    hide-footer
    hide-header
    centered
    @hide="handleHidden"
  >
    <div
      class="text-center text-[#2c2c2c]"
    >
      <img
        :src="isSuccess ? 'https://storage.googleapis.com/komerce/core/icon-success-verified.svg' : 'https://storage.googleapis.com/komerce/assets/icons/warning.svg'"
        alt="Komerce"
        class="w-[100px] h-[100px] mx-auto my-2"
      >
      <h3
        v-if="isSuccess"
        class="text-[#2c2c2c] text-lg font-semibold"
      >
        {{ title }}
      </h3>
      <p class="flex items-center gap-2 justify-center">
        <b-spinner
          v-if="isProcessing"
          variant="primary"
          small
        />
        {{ message }}
      </p>
      <div
        v-if="isSuccess"
        class="flex items-center gap-4 justify-center"
      >
        <router-link
          :to="{ name: 'detail-low-moving-stock', params: { id: refId } }"
          class="btn btn-outline-primary btn-sm min-w-[130px] !py-[11px]"
        >
          Detail Pembayaran
        </router-link>
      </div>
      <p
        v-if="isCountdownShown"
        class="text-[#2c2c2c] text-sm italic mt-[20px]"
      >
        {{ countDownMessage }} {{ countDown }} detik...
      </p>
      <div
        v-if="isConfirming"
        class="flex items-center gap-4 justify-center"
      >
        <b-button
          variant="outline-primary"
          @click="handleCancelPayment"
        >
          Tidak
        </b-button>
        <b-button
          variant="primary"
          @click="handleConfirmPayment"
        >
          Ya</b-button>
      </div>
      <div
        v-if="isNotEnoughBalance"
        class="flex items-center gap-4 justify-center"
      >
        <b-button
          variant="outline-primary"
          @click="handleCancelPayment"
        >
          Kembali
        </b-button>
        <router-link
          :to="{ name: 'saldo', params: { topup: true } }"
          class="btn btn-primary btn-sm min-w-[130px] !py-[11px]"
        >
          Top Up Saldo
        </router-link>
      </div>
    </div></b-modal>
</template>

<script>
/**
 * Vue Component untuk menampilkan proses pembayaran tagihan dengan flow sebagai berikut:
 *  CONFIRMING -> PROCESSING -> SUCCESS
 *                           -> FAILED -> IS_NOT_ENOUGH_BALANCE
 *
 * @props {Boolean} isFromTagihan - Apakah dari halaman Tagihan atau bukan(halaman Detail Tagihan)
 * @props {Function} onClose - Fungsi untuk menutup modal
 * @props {String|Number} invoiceId - ID tagihan
 *
 * @data {String} title - Judul modal
 * @data {String} message - Pesan modal
 * @data {String} countDownMessage - Pesan menghitung mundur
 * @data {Number} countDown - Menghitung mundur
 * @data {Object} countdownInstance - Instance countdown
 */
import { startCountdown } from '@/@core/utils/countdown'

export const PAYMENT_STATUS = Object.freeze({
  CONFIRMING: 'confirming',
  PROCESSING: 'processing',
  SUCCESS: 'success',
  FAILED: 'failed',
  IS_NOT_ENOUGH_BALANCE: 'is_not_enough_balance',
})

export default {
  name: 'PayInvoiceProcess',
  props: {
    isFromTagihan: {
      type: Boolean,
      default: false,
    },
    onClose: {
      type: Function,
      default: () => {},
    },
    invoiceId: {
      type: [String, Number],
      default: '',
    },
    refId: {
      type: [String, Number],
      default: '',
    },
  },
  data() {
    return {
      countDown: 5,
      fromTagihan: this.isFromTagihan,
      title: '',
      message: 'Apakah Kamu Yakin Melakukan Pembayaran?',
      countDownMessage: 'Mengalihkan dalam',
      countdownInstance: null,
      status: PAYMENT_STATUS.CONFIRMING,
      isCountdownShown: false,
    }
  },
  computed: {
    isConfirming() {
      return this.status === PAYMENT_STATUS.CONFIRMING
    },
    isProcessing() {
      return this.status === PAYMENT_STATUS.PROCESSING
    },
    isSuccess() {
      return this.status === PAYMENT_STATUS.SUCCESS
    },
    isFailed() {
      return this.status === PAYMENT_STATUS.FAILED
    },
    isNotEnoughBalance() {
      return this.status === PAYMENT_STATUS.IS_NOT_ENOUGH_BALANCE
    },
  },
  beforeDestroy() {
    this.clearCountdown()
  },
  methods: {
    startCountdown,
    /**
     * Menghapus countdown yang sedang berjalan
     */
    clearCountdown() {
      if (this.countdownInstance) {
        clearInterval(this.countdownInstance.intervalId)
        clearTimeout(this.countdownInstance.timeoutId)
        this.countdownInstance = null
        this.countDown = 5
      }
    },
    /**
     * Mengatur ulang popup
     */
    resetPopup() {
      this.countDown = 5
      this.message = 'Apakah Kamu Yakin Melakukan Pembayaran?'
      this.countDownMessage = 'Mengalihkan dalam'
      this.countdownInstance = null
      this.status = PAYMENT_STATUS.CONFIRMING
      this.isCountdownShown = false
    },
    /**
     * Menutup popup
     */
    hide() {
      this.$refs['pay-invoice-process'].hide()
      this.clearCountdown()
    },
    /**
     * Menampilkan popup
     */
    show() {
      this.resetPopup()
      this.$refs['pay-invoice-process'].show()
    },
    /**
     * Menjalankan countdown
     * @param {Object} options - Options countdown
     * @param {Number} options.timeout - Waktu countdown dalam milidetik
     * @param {String} options.message - Pesan countdown
     * @param {Function} options.onFinish - Fungsi yang akan dipanggil ketika countdown selesai
    */
    runCountdown({ timeout = 5000, message = 'mengalihkan dalam', onFinish = () => {} }) {
      this.countDown = timeout / 1000
      this.countDownMessage = message
      this.isCountdownShown = true
      this.countdownInstance = startCountdown(5000, () => {
        onFinish()
        this.hide()
      }, secondsLeft => {
        this.countDown = secondsLeft
      })
    },
    /**
     * Menutup popup dari parent component
     */
    handleHidden() {
      this.onClose({
        status: this.status,
      })
    },
    /**
     * Menutup popup dari button "Tidak" atau "Kembali"
     */
    handleCancelPayment() {
      this.hide()
    },
    /**
     * Memproses pembayaran tagihan
     */
    handleConfirmPayment() {
      this.status = PAYMENT_STATUS.PROCESSING
      this.message = 'Memproses pembayaran...'
      this.mutateInvoicePayment(this.invoiceId)
    },
    /**
     * Memproses pembayaran tagihan
     * @param {String|Number} id - ID tagihan
     */
    mutateInvoicePayment(id) {
      this.$http_new.patch(`/auth/api/v1/user-balance-invoices/${id}/pay`).then(res => {
        this.status = PAYMENT_STATUS.SUCCESS
        this.title = 'Pembayaran Berhasil! '
        this.message = 'Terima kasih telah melakukan pembayaran. Berikut detail pembayaran kamu'
        this.runCountdown({
          timeout: 5000,
          message: 'kembali ke halaman Tagihan dalam',
          onFinish: () => {
            this.$router.push({ name: 'tagihan' })
          },
        })
      }).catch(err => {
        this.status = PAYMENT_STATUS.FAILED
        if (err.response.data.meta.code === 404) {
          this.title = 'Pembayaran Gagal!'
          this.message = 'Tagihan tidak ditemukan'
          this.runCountdown({
            timeout: 5000,
            message: 'kembali ke halaman Tagihan dalam',
            onFinish: () => {
              if (this.fromTagihan) {
                this.hide()
                return
              }
              this.$router.push({ name: 'tagihan' })
            },
          })
          return
        }
        if (
          err.response.data.meta.code === 400
          && err.response.data.meta.message === 'Saldo tidak mencukupi untuk membayar invoice ini.'
        ) {
          this.title = 'Saldo Belum Mencukupi'
          this.message = 'Kamu harus mengisi saldo dulu ya, sebelum melanjutkan pembayaran tagihan ini.'
          this.status = PAYMENT_STATUS.IS_NOT_ENOUGH_BALANCE
          return
        }
        if (err.response.data.meta.code === 400) {
          this.title = 'Pembayaran Gagal!'
          this.message = err.response.data.meta.message
          this.runCountdown({
            timeout: 5000,
            message: 'menutup popup dalam',
            onFinish: () => {
              if (this.fromTagihan) {
                this.hide()
                return
              }
              this.$router.push({ name: 'tagihan' })
            },
          })
          return
        }
        this.title = 'Pembayaran Gagal!'
        this.message = 'Gagal membayar tagihan, silahkan coba lagi.'
        this.runCountdown({
          timeout: 5000,
          message: 'menutup popup dalam',
          onFinish: () => {
            this.hide()
          },
        })
      })
    },
  },
}
</script>
